import React from 'react';
import PropTypes from 'prop-types';
import cloneDeep from 'lodash/cloneDeep';
import { Container, Input, InputGroup, InputGroupAddon, Button } from '@sm/wds-react';
import { IconX, IconArrowDown, IconArrowUp, IconPlusCircle } from '@sm/wds-icons';
import { SINGLE_CHOICE } from '../../pages/NewSurvey/questionTypes';

const MultipleChoice = ({ question, surveyType, updateQuestion, maxAnsChoices, readOnlySurvey }) => {
  const handleAnswerChange = (idx, event) => {
    const currentQuestion = cloneDeep(question);
    const answers = [...currentQuestion.answers];
    answers[idx].answer = event.target.value;

    currentQuestion.answers = answers;
    updateQuestion(currentQuestion);
  };

  const moveAnswer = (idx, down) => {
    if (idx === 0 && !down) {
      return;
    }
    if (idx === question.answers.length - 1 && down) {
      return;
    }

    const currentQuestion = cloneDeep(question);
    const answers = [...currentQuestion.answers];

    const answer = answers[idx];
    const to = down ? idx + 1 : idx - 1;
    answers.splice(idx, 1);
    answers.splice(to, 0, answer);

    currentQuestion.answers = answers;
    updateQuestion(currentQuestion);
  };

  const deleteAnswer = idx => {
    const currentQuestion = cloneDeep(question);
    const answers = [...currentQuestion.answers];
    answers.splice(idx, 1);

    currentQuestion.answers = answers;
    updateQuestion(currentQuestion);
  };

  const addAnswer = () => {
    const currentQuestion = cloneDeep(question);
    const answers = [...currentQuestion.answers];
    answers.push({ answer: '', score: '' });

    currentQuestion.answers = answers;
    updateQuestion(currentQuestion);
  };

  const handleScoreChange = (idx, event) => {
    const val = event.target.value.replace(/[^0-9]+/g, '');
    const currentQuestion = cloneDeep(question);
    const answers = [...currentQuestion.answers];
    answers[idx].score = val;

    currentQuestion.answers = answers;
    updateQuestion(currentQuestion);
  };

  return (
    <Container data-testid="MultipleChoice">
      {question.answers.length > maxAnsChoices && (
        <div className="sm-new-survey__error">
          Multiple choice will only display a maximum of&nbsp;
          {maxAnsChoices}
          &nbsp;poll choices
        </div>
      )}
      {question.answers.map((answer, index) => {
        if (index < maxAnsChoices) {
          return (
            <Container key={`question_${question.id}_${index + 1}`} mb={4}>
              <InputGroup
                className={`sm-new-survey__choice ${surveyType === 'quiz' ? 'sm-new-survey__choice-has-score' : ''} ${
                  index === 0 ? 'sm-new-survey__choice-first' : ''
                }`}
                stretched
                size="sm"
                disabled={readOnlySurvey}
              >
                <Container className="sm-new-survey__choice-container">
                  <Input
                    id={`question-${question.id}-answer${index + 1}`}
                    className="sm-new-survey__choice-input sm-new-survey__input"
                    stretched
                    size="sm"
                    value={answer.answer}
                    placeholder="Enter choice"
                    label={index === 0 ? 'Choices' : undefined}
                    onChange={event => handleAnswerChange(index, event)}
                    disabled={readOnlySurvey}
                  />
                  <InputGroupAddon>
                    {surveyType === 'quiz' && (
                      <Input
                        id={`question-${question.id}-score${index + 1}`}
                        className="sm-new-survey__score"
                        size="sm"
                        value={answer.score}
                        placeholder="0"
                        label={index === 0 ? 'Score' : undefined}
                        onChange={event => handleScoreChange(index, event)}
                        disabled={readOnlySurvey}
                      />
                    )}
                    <div className="sm-new-survey__choice-options">
                      {index !== 0 && (
                        <Button
                          className="sm-new-survey__choice-options-button"
                          size="sm"
                          variant="text"
                          onClick={() => moveAnswer(index)}
                          tabIndex="-1"
                        >
                          <span className="sm-new-survey__choice-options-button-icon">
                            <IconArrowUp />
                          </span>
                        </Button>
                      )}
                      {index !== question.answers.length - 1 && (
                        <Button
                          className="sm-new-survey__choice-options-button"
                          size="sm"
                          variant="text"
                          onClick={() => moveAnswer(index, true)}
                          tabIndex="-1"
                        >
                          <span className="sm-new-survey__choice-options-button-icon">
                            <IconArrowDown />
                          </span>
                        </Button>
                      )}
                      {question.answers.length > 2 && (
                        <Button
                          className="sm-new-survey__choice-options-button"
                          size="sm"
                          variant="text"
                          onClick={() => deleteAnswer(index)}
                          tabIndex="-1"
                        >
                          <span className="sm-new-survey__choice-options-button-icon">
                            <IconX />
                          </span>
                        </Button>
                      )}
                    </div>
                  </InputGroupAddon>
                </Container>
              </InputGroup>
            </Container>
          );
        }
        return null;
      })}
      {question.answers.length < maxAnsChoices && !readOnlySurvey && (
        <Button size="sm" variant="text" onClick={() => addAnswer()} className="sm-new-survey__add-anschoice">
          <IconPlusCircle />
          Choice
        </Button>
      )}
    </Container>
  );
};

MultipleChoice.propTypes = {
  question: PropTypes.shape({
    id: PropTypes.string,
    question: PropTypes.string,
    questionType: PropTypes.string,
    answers: PropTypes.arrayOf(
      PropTypes.shape({
        answer: PropTypes.string,
        score: PropTypes.string,
      })
    ),
    min: PropTypes.number,
    max: PropTypes.number,
    allowComments: PropTypes.bool,
    required: PropTypes.bool,
    isNew: PropTypes.bool,
    isUpdated: PropTypes.bool,
  }),
  surveyType: PropTypes.string,
  updateQuestion: PropTypes.func,
  maxAnsChoices: PropTypes.number,
  readOnlySurvey: PropTypes.bool,
};

MultipleChoice.defaultProps = {
  question: {
    id: '',
    question: '',
    questionType: SINGLE_CHOICE,
    answers: [
      { answer: '', score: '' },
      { answer: '', score: '' },
      { answer: '', score: '' },
    ],
    min: 0,
    max: 100,
    allowComments: false,
    required: false,
    isNew: true,
    isUpdated: false,
  },
  surveyType: 'poll',
  updateQuestion: () => {},
  maxAnsChoices: 50,
  readOnlySurvey: false,
};

export default MultipleChoice;
